/***
 * There are multiple response formats, however they differ only in few fields.
 * Would be nice to rework API to be consistent.
 *
 * For now there are 4 item list response types:
 *   1) Search result (marketplace)
 *   2) List of items (user profile)
 *   3) Featured items (homepage)
 *   4) Items in collections based in given Collectible (database)
 */

import { ApiClient } from "@/services/apiClient";
import { AxiosError } from "axios";
import { type CollectionItemId } from "@/services/collection/item/collectionItemTypes";
import { type PublicCollectionItem, type PublicCollectionItem_FromCollectible, PublicItemNotFoundException } from "@/services/collection/item/public/publicCollectionItemTypes";
import type { OnlyPublicProperties } from "@/types";
import { PublicCollectionItemsFilter } from "@/services/collection/item/public";
import { z } from "zod";
import { WholeNumber_Schema } from "@/utilities/schema";
import { PublicCollectionItem_FeaturedListItem_Schema, PublicCollectionItem_FromCollectible_Schema, PublicCollectionItem_ListItem_Schema, PublicCollectionItem_SearchListItem_Schema } from "@/services/collection/item/public/publicCollectionItemSchema";
import { type CollectibleId } from "@/services/collectibles";

export async function fetchPublicCollectionItemById(itemId: CollectionItemId): Promise<PublicCollectionItem> {
  const url = `api-public/user/collector-item`;
  const params = { id: itemId };
  return ApiClient.getPublic<PublicCollectionItem>(url, params)
    .then(({ data }) => data)
    .catch((error: unknown) => {
      if (error instanceof AxiosError && error.response?.status === 404) {
        throw new PublicItemNotFoundException();
      } else {
        throw error;
      }
    });
}

/**
 * =============================================================================
 * Featured items (homepage)
 * =============================================================================
 */
const ApiResponse_FeaturedItemList_Schema = z.object({
  count: WholeNumber_Schema,
  result: PublicCollectionItem_FeaturedListItem_Schema.array(),
});
type ApiResponse_FeaturedItemList = z.infer<typeof ApiResponse_FeaturedItemList_Schema>;

export const fetchFeaturedCollectionItems = async (): Promise<ApiResponse_FeaturedItemList> => {
  const url = `api-public/user/collection-listed-item/top-pick`;
  return ApiClient.getPublic<ApiResponse_FeaturedItemList>(url, {}).then(({ data }) => ApiResponse_FeaturedItemList_Schema.parse(data));
  // Intentionally no catch() ... Errors should be handled directly in items grid/list component.
};

/**
 * =============================================================================
 * Search result (old marketplace)
 * =============================================================================
 */
const ApiResponse_SearchItemList_Schema = z.object({
  count: WholeNumber_Schema,
  result: PublicCollectionItem_SearchListItem_Schema.array(),
});
type ApiResponse_SearchItemList = z.infer<typeof ApiResponse_SearchItemList_Schema>;

export const fetchPublicCollectionItems = async (filter: OnlyPublicProperties<PublicCollectionItemsFilter.Filter>): Promise<ApiResponse_SearchItemList> => {
  const url = `api-public/user/collection-listed-item/search`;
  const params = PublicCollectionItemsFilter.createApiParams(filter);
  return ApiClient.getPublic<ApiResponse_SearchItemList>(url, params).then(({ data }) => ApiResponse_SearchItemList_Schema.parse(data));
  // Intentionally no catch() ... Errors should be handled directly in items grid/list component.
};

/**
 * =============================================================================
 * List of items (user profile)
 * =============================================================================
 */
const ApiResponse_ItemList_Schema = z.object({
  count: WholeNumber_Schema,
  result: PublicCollectionItem_ListItem_Schema.array(),
});
type ApiResponse_ItemList = z.infer<typeof ApiResponse_ItemList_Schema>;

export const fetchPublicCollectionItemsForUser = async (filter: OnlyPublicProperties<PublicCollectionItemsFilter.Filter>): Promise<ApiResponse_ItemList> => {
  const url = `api-public/user/collection-listed-item`;
  const params = PublicCollectionItemsFilter.createApiParams(filter);
  return ApiClient.getPublic<ApiResponse_ItemList>(url, params).then(({ data }) => ApiResponse_ItemList_Schema.parse(data));
  // Intentionally no catch() ... Errors should be handled directly in items grid/list component.
};

/**
 * =========================================================================
 * List of Collection items based on given Collectible.
 * =========================================================================
 */

const ApiResponse_PublicCollectionItemFromCollectibleList_Schema = z.object({
  collection: PublicCollectionItem_FromCollectible_Schema.array(),
  totalCount: WholeNumber_Schema,
});

type ApiResponse_CollectionItemFromCollectible_List = z.infer<typeof ApiResponse_PublicCollectionItemFromCollectibleList_Schema>;

export const fetchForCollectible = async (id: CollectibleId): Promise<PublicCollectionItem_FromCollectible[]> => {
  const url = "/api-public/collection/in-collection";
  const query = {
    collectible: id,
    page: 0,
    perPage: 9000, // 'perPage' is required, but since the total number of items is unknown, we load the first x items.
  };
  return ApiClient.getPublic<ApiResponse_CollectionItemFromCollectible_List>(url, query).then(({ data }) => ApiResponse_PublicCollectionItemFromCollectibleList_Schema.parse(data).collection);
};
